import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { userAPI } from '../services/api';
import './AddressPrivacySetup.css';

const AddressPrivacySetup: React.FC = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [visibilityLevel, setVisibilityLevel] = useState<number>(4); // Default: Street with number
  const [accessCode, setAccessCode] = useState('');
  const [apartmentInfo, setApartmentInfo] = useState('');
  const [loading, setLoading] = useState(false);

  // Clear the registration flag and scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
    sessionStorage.removeItem('justRegistered');
  }, []);

  // Get filtered address based on visibility level
  const getFilteredAddress = () => {
    if (!user?.address) return '';

    const { street_name, street_address, city, district, postal_code, country } = user.address;

    switch (visibilityLevel) {
      case 1: // City only
        return `${city}, ${country}`;

      case 2: // District
        // Use district if available (includes arrondissements for Paris)
        if (district) {
          return `${district}, ${city}, ${country}`;
        }
        // Fallback to city only if no district available
        return `${city}, ${country}`;

      case 3: // Street without number
        if (street_name) {
          return `${street_name}, ${postal_code || ''} ${city}, ${country}`;
        }
        return `${postal_code || ''} ${city}, ${country}`;

      case 4: // Street with number
        if (street_address) {
          return `${street_address}, ${postal_code || ''} ${city}, ${country}`;
        }
        return `${city}, ${country}`;

      case 5: // Full access (with codes)
        let fullAddress = street_address ? `${street_address}, ` : '';
        fullAddress += `${postal_code || ''} ${city}, ${country}`;
        if (apartmentInfo) fullAddress += `\n${apartmentInfo}`;
        if (accessCode) fullAddress += `\nCode : ${accessCode}`;
        return fullAddress;

      default:
        return `${city}, ${country}`;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Save privacy settings to backend
      await userAPI.updateAddressPrivacy({
        default_visibility_level: visibilityLevel,
        access_code: accessCode || undefined,
        apartment_number: apartmentInfo || undefined,
      });

      // Set flag to show welcome modal on map
      sessionStorage.setItem('showWelcomeModal', 'true');

      // Redirect to map
      navigate('/map');
    } catch (error) {
      console.error('Error saving privacy settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    navigate('/map');
  };

  const getLevelLabel = (level: number) => {
    switch (level) {
      case 1: return t('addressPrivacy.levels.city.label');
      case 2: return t('addressPrivacy.levels.district.label');
      case 3: return t('addressPrivacy.levels.street.label');
      case 4: return t('addressPrivacy.levels.streetNumber.label');
      case 5: return t('addressPrivacy.levels.full.label');
      default: return '';
    }
  };

  const getLevelDescription = (level: number) => {
    switch (level) {
      case 1: return t('addressPrivacy.levels.city.description');
      case 2: return t('addressPrivacy.levels.district.description');
      case 3: return t('addressPrivacy.levels.street.description');
      case 4: return t('addressPrivacy.levels.streetNumber.description');
      case 5: return t('addressPrivacy.levels.full.description');
      default: return '';
    }
  };

  return (
    <div className="privacy-setup-container">
      <div className="privacy-setup-card">
        <h1>🔐 {t('addressPrivacy.title')}</h1>
        <p className="subtitle">
          {t('addressPrivacy.subtitle')}
        </p>

        <form onSubmit={handleSubmit} className="privacy-form">
          {/* Address Display */}
          <div className="address-section">
            <h3>📍 {t('addressPrivacy.fullAddress')}</h3>
            <div className="complete-address">
              <p>
                {user?.address?.street_address && `${user.address.street_address}, `}
                {user?.address?.postal_code} {user?.address?.city}, {user?.address?.country}
              </p>
            </div>
          </div>

          {/* Additional Info Section */}
          <div className="additional-info-section">
            <h3>🏢 {t('addressPrivacy.additionalInfo')}</h3>
            <p className="info-explanation">
              {t('addressPrivacy.additionalInfoHint')}
            </p>

            <div className="form-group">
              <label htmlFor="apartmentInfo">
                {t('addressPrivacy.apartmentLabel')}
              </label>
              <input
                type="text"
                id="apartmentInfo"
                value={apartmentInfo}
                onChange={(e) => setApartmentInfo(e.target.value)}
                placeholder={t('addressPrivacy.apartmentPlaceholder')}
              />
            </div>

            <div className="form-group">
              <label htmlFor="accessCode">
                {t('addressPrivacy.accessCodeLabel')}
              </label>
              <input
                type="text"
                id="accessCode"
                value={accessCode}
                onChange={(e) => setAccessCode(e.target.value)}
                placeholder={t('addressPrivacy.accessCodePlaceholder')}
              />
            </div>
          </div>

          {/* Visibility Level Slider */}
          <div className="visibility-section">
            <h3>👁️ {t('addressPrivacy.visibilityLevel')}</h3>
            <p className="visibility-tip">
              💡 {t('addressPrivacy.visibilityTip')}
            </p>

            <div className="level-slider-container">
              <div className="level-labels">
                <span className="level-label">{t('addressPrivacy.visibilityScale.private')}</span>
                <span className="level-label">{t('addressPrivacy.visibilityScale.open')}</span>
              </div>

              <input
                type="range"
                min="1"
                max="5"
                value={visibilityLevel}
                onChange={(e) => setVisibilityLevel(Number(e.target.value))}
                className="level-slider"
              />

              <div className="level-indicators">
                {[1, 2, 3, 4, 5].map((level) => (
                  <div
                    key={level}
                    className={`level-indicator ${visibilityLevel >= level ? 'active' : ''}`}
                    onClick={() => setVisibilityLevel(level)}
                    style={{ cursor: 'pointer' }}
                  >
                    {level}
                  </div>
                ))}
              </div>

              <div className="level-info">
                <strong>Niveau {visibilityLevel} : {getLevelLabel(visibilityLevel)}</strong>
                <p>{getLevelDescription(visibilityLevel)}</p>
                {visibilityLevel === 5 && (
                  <p className="level-warning">⚠️ {t('addressPrivacy.level5Warning')}</p>
                )}
              </div>
            </div>

            {/* Address Preview */}
            <div className="address-preview">
              <h4>👀 {t('addressPrivacy.preview')}</h4>
              <div className="preview-box">
                <pre>{getFilteredAddress()}</pre>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="action-buttons">
            <button type="button" onClick={handleSkip} className="btn-skip">
              {t('addressPrivacy.skip')}
            </button>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? t('addressPrivacy.saving') : t('addressPrivacy.saveAndContinue')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddressPrivacySetup;
